import { Modal, Form } from "react-bootstrap";
import { useEffect, useMemo, useState } from "react";
import { ServerRequestsSubscriptionGroupsResponse } from "./shared/api/axios-client";
import { Button } from "./Controls/Button";
import { HeaderText } from "./Controls/HeaderText";

type Props = {
  show: boolean;
  onClose: () => void;
  group?: ServerRequestsSubscriptionGroupsResponse;
  existingGroups: ServerRequestsSubscriptionGroupsResponse[];
  onSubmit: (name: string, groupId?: number) => Promise<void> | void;
};

export function GroupModal({
  show,
  onClose,
  group,
  existingGroups,
  onSubmit,
}: Props) {
  const isEdit = !!group;

  const [name, setName] = useState("");
  const [touched, setTouched] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (group) {
      setName(group.name!);
    } else {
      setName("");
    }
    setTouched(false);
  }, [group, show]);

  const error = useMemo(() => {
    if (!touched) return "";

    if (!name.trim()) return "Group name is required.";
    if (name.length < 3) return "Group name must be at least 3 characters.";
    if (name.length > 64) return "Group name must be less than 64 characters.";

    const duplicate = existingGroups.some(
      g =>
        g.name!.toLowerCase() === name.toLowerCase() &&
        g.id !== group?.id
    );

    if (duplicate) return "A group with this name already exists.";

    return "";
  }, [name, touched, existingGroups, group]);

  const isValid = !error && name.trim().length > 0;

  const handleSubmit = async () => {
    if (!isValid) return;

    setSubmitting(true);
    await onSubmit(name.trim(), group?.id);
    setSubmitting(false);
    onClose();
  };

  return (
    <Modal show={show} onHide={onClose} centered>
      <Modal.Header closeButton>
        <HeaderText>
          {isEdit ? "Edit Group" : "Add Group"}
        </HeaderText>
      </Modal.Header>

      <Modal.Body>
        <p className="text-muted small mb-3">
          You can add groups to help organize your parkers, but only the operator can adjust contracts and pricing tiers.
        </p>
        <Form>
          <Form.Group>
            <Form.Label>Group name</Form.Label>
            <Form.Control
              value={name}
              autoFocus
              placeholder="Enter group name"
              isInvalid={!!error}
              onChange={(e) => {
                setName(e.target.value);
                setTouched(true);
              }}
            />
            <Form.Control.Feedback type="invalid">
              {error}
            </Form.Control.Feedback>
          </Form.Group>
        </Form>
      </Modal.Body>

      <Modal.Footer>
        <Button
          onClick={onClose}
          disabled={submitting}
        >
          Cancel
        </Button>

        <Button
          className="btn-main"
          disabled={!isValid || submitting}
          onClick={handleSubmit}
        >
          {isEdit ? "Save Changes" : "Add Group"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}